import { IF, NO, UITextView, UIView, YES } from "uicore-ts"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHFileListView } from "../../Custom components/RH/RHFileListView"
import { RHNotificationBadge } from "../../Custom components/RH/RHNotificationBadge"
import { formatRHDateTime } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import {
    RHCompanyQuestionsCardViewTokens,
    RHQuestionAnswerPairViewTokens
} from "../../Custom components/RH/Utils_slop/RHViewTokens"
import {
    ProcurementQuestionAndAnswerThread,
    RHProcurementAmendmentNotificationChange
} from "../../SocketClientFunctions"
import { RHQuestionNumberFormatter } from "./RHQuestionNumberFormatter"

export interface QuestionAnswerPair {
    question: ProcurementQuestionAndAnswerThread
    isAnswered: boolean
}

/**
 * Individual question-answer pair view with separator
 */
export class QuestionAnswerPairView extends UIView {
    
    // Combined question title (same pattern as the message dialog):
    // "Küsimus nr N | <ärinimi> (<e-post>) | <ajatempel>". Single line; "Küsimus nr N"
    // is emphasised with <b>, the rest keeps the regular metadata weight. The old
    // separate right-aligned questionDateLabel is merged into this title.
    readonly questionMetadataLabel = new UITextView()
        .configuredWithObject({
            style: {
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHCompanyQuestionsCardViewTokens.lastUpdateFontWeight,
                fontFamily: RHFont.familyBase,
                margin: "0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
            }
        })
        .addedAsSubviewToView(this)

    // Merged into questionMetadataLabel above — kept commented for reference.
    // readonly questionDateLabel = new UITextView()
    //     .configuredWithObject({
    //         textAlignment: UITextView.textAlignment.right,
    //         style: {
    //             fontSize: RHQuestionAnswerPairViewTokens.metadataDateFontSize,
    //             fontWeight: RHQuestionAnswerPairViewTokens.metadataDateFontWeight,
    //             fontFamily: RHQuestionAnswerPairViewTokens.metadataDateFontFamily,
    //             color: RHQuestionAnswerPairViewTokens.metadataDateTextColor.stringValue,
    //             margin: "0"
    //         }
    //     })
    //     .addedAsSubviewToView(this)
    
    readonly questionBanner = new RHAlertBannerView()
        .addedAsSubviewToView(this)
    
    readonly questionFilesListView = new RHFileListView()
        .configuredWithObject({
            variant: RHFileListView.variant.compactDownload
        })
        .addedAsSubviewToView(this)
    
    readonly answerSectionLabel = new UITextView()
        .configuredWithObject({
            text: "Hankija vastus",
            style: {
                fontSize: RHFont.sizeXSmall,
                fontWeight: "600",
                fontFamily: RHFont.familyBase,
                color: RHColors.successGreenDark.stringValue,
                margin: "0"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly answerDateLabel = new UITextView()
        .configuredWithObject({
            text: "",
            textAlignment: UITextView.textAlignment.right,
            style: {
                fontSize: RHFont.sizeXSmall,
                fontWeight: RHCompanyQuestionsCardViewTokens.lastUpdateFontWeight,
                fontFamily: RHFont.familyBase,
                color: RHColors.textTimestamp.stringValue,
                margin: "0"
            }
        })
        .addedAsSubviewToView(this)
    
    readonly answerBanner = new RHAlertBannerView()
        .addedAsSubviewToView(this)
    
    readonly answerFilesListView = new RHFileListView()
        .configuredWithObject({
            variant: RHFileListView.variant.compactDownload
        })
        .addedAsSubviewToView(this)
    
    readonly writeAnswerButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Vasta" },
            variant: "messageAnswer",
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            }
        })
    
    readonly copyAnswerButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Kopeeri" },
            variant: "messageAnswer",
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            }
        })
    
    // Separator line at the bottom
    readonly separatorLine = new UIView()
        .configuredWithObject({
            backgroundColor: RHColors.dividerAlpha50,
            style: { height: "1px" }
        })
    
    
    answerButtonWasPressed?: () => void
    
    get questionId(): string {
        return this.questionData._id
    }
    
    highlight() {
        const element = this.viewHTMLElement
        const original = element.style.transition
        element.style.transition = "background-color 0.3s ease-in-out"
        element.style.backgroundColor = "rgba(99, 102, 241, 0.15)"
        setTimeout(() => {
            element.style.backgroundColor = ""
            setTimeout(() => {
                element.style.transition = original
            }, 300)
        }, 1200)
        element.scrollIntoView({ behavior: "smooth", block: "center" })
    }
    
    override _focusManager: RHFocusManager = new RHFocusManager(() => {
        if (this._isLocked) {
            return []
        }
        if (this._isBroadcast) {
            if (this._canAnswerMessages && this.copyAnswerButton.superview === this) {
                return [this.copyAnswerButton]
            }
            return []
        }
        if (this.isAnswered) {
            if (this._canAnswerMessages && this.copyAnswerButton.superview === this) {
                return [this.copyAnswerButton]
            }
        }
        else {
            if (this._canAnswerMessages && this.writeAnswerButton.superview === this) {
                return [this.writeAnswerButton]
            }
        }
        return []
    })
    
    private readonly isAnswered: boolean
    hasNextQuestion = NO
    private readonly questionData: ProcurementQuestionAndAnswerThread
    private readonly _isBroadcast: boolean
    readonly _isAutomaticMessage: boolean
    private _isLocked = NO
    _isCurrentUserActingAsProcurer = NO
    private _canAnswerMessages = NO
    
    
    constructor(questionData: ProcurementQuestionAndAnswerThread, isAnswered: boolean, hasNextQuestion: boolean) {
        super()
        
        this.isAnswered = isAnswered
        this.questionData = questionData
        this.hasNextQuestion = hasNextQuestion
        this._isBroadcast = questionData.threadType === "procurerBroadcast"
        this._isAutomaticMessage =
            questionData.automaticMessage?.kind === "procurementAmendment"

        const questionDate = new Date(questionData.createdAt)

        // Title. A procurer broadcast gets a fixed heading; a tenderer question gets the
        // combined "Küsimus nr N | <ärinimi> (<e-post>) | <ajatempel>" title.
        // Business name is the convention everywhere a sender is identified.
        if (this._isBroadcast) {
            this.questionMetadataLabel.text =
                `<b>Hankija sõnum kõikidele registreerunud pakkujatele</b> | ${formatRHDateTime(questionDate)}`
        }
        else {
            const questionNumberText = RHQuestionNumberFormatter.metadataTextForQuestion(questionData)
            const senderCompanyName = questionData.authorCompanyName || "Tundmatu pakkuja"
            let senderText = senderCompanyName
            if (questionData.authorName) {
                senderText = `${senderCompanyName} (${questionData.authorName})`
            }
            this.questionMetadataLabel.text =
                `<b>${questionNumberText}</b> | ${senderText} | ${formatRHDateTime(questionDate)}`
        }
        // this.questionDateLabel.text = formatRHDateTime(questionDate)   // merged into the title above
        
        // Configure question banner
        let messageText = questionData.bodyText
        if (questionData.automaticMessage?.kind === "procurementAmendment") {
            messageText = QuestionAnswerPairView._bannerHTMLForAmendmentMessage(
                questionData.automaticMessage,
                questionData.authorName
            )
        }
        else if (this._isBroadcast && (
            messageText.startsWith("Hange lõpetati ilma lepinguta.\n\nPõhjus: ") ||
            messageText.startsWith("Täpsustav voor lõpetati ilma lepinguta. Hanke teiste osade seisund sellest ei muutu.\n\nPõhjus: ")
        )) {
            messageText = RHTextSanitizer.escapedHTML(messageText)
                .replace("\n\nPõhjus:", "<br>\n<strong>Põhjus:</strong>")
        }
        this.questionBanner.configureBannerWithObject({
            text: messageText,
            fontSize: RHFont.sizeSmall,
            fontWeight: RHQuestionAnswerPairViewTokens.messageTextFontWeight,
            fontFamily: RHFont.familyBase
        })
        this._updateBannerTypes()
        
        // Configure question file list
        if (questionData.bodyFileIds.length > 0) {
            this.questionFilesListView.downloadEnabled = YES
            this.questionFilesListView.deleteEnabled = NO
            this.questionFilesListView.loadFilesWithIds(questionData.bodyFileIds)
        }
        else {
            this.questionFilesListView.removeFromSuperview()
        }
        
        // Setup answer button
        // noinspection JSConstantReassignment
        this.writeAnswerButton.controlEventTargetAccumulator.PrimaryActionTriggered = (sender, event) => {
            if (this.answerButtonWasPressed) {
                this.answerButtonWasPressed()
            }
        }
        
        if (isAnswered && questionData.answerText) {
            this.answerSectionLabel.hidden = NO
            this.answerDateLabel.hidden = NO
            this.answerBanner.hidden = NO
            this.answerBanner.configureBannerWithObject({
                text: questionData.answerText,
                fontSize: RHFont.sizeSmall,
                fontWeight: RHQuestionAnswerPairViewTokens.messageTextFontWeight,
                fontFamily: RHFont.familyBase
            })
            
            const answerDate = new Date(questionData.updatedAt || questionData.createdAt)
            this.answerDateLabel.text = formatRHDateTime(answerDate)
            
            if (questionData.answerFileIds.length > 0) {
                this.answerFilesListView.downloadEnabled = YES
                this.answerFilesListView.deleteEnabled = NO
                this.answerFilesListView.loadFilesWithIds(questionData.answerFileIds)
            }
            else {
                this.answerFilesListView.removeFromSuperview()
            }
        }
        else {
            this.answerSectionLabel.hidden = YES
            this.answerDateLabel.hidden = YES
            this.answerBanner.hidden = YES
            this.answerFilesListView.removeFromSuperview()
        }
        
        this._updateActionButtonVisibility()
    }
    
    
    set isLocked(value: boolean) {
        this._isLocked = value
        this._updateActionButtonVisibility()
    }
    
    get isLocked() {
        return this._isLocked
    }
    
    set isCurrentUserActingAsProcurer(isCurrentUserActingAsProcurer: boolean) {
        this._isCurrentUserActingAsProcurer = isCurrentUserActingAsProcurer
        this._updateBannerTypes()
        this._updateActionButtonVisibility()
    }
    
    get isCurrentUserActingAsProcurer() {
        return this._isCurrentUserActingAsProcurer
    }

    set canAnswerMessages(value: boolean) {
        this._canAnswerMessages = value
        this._updateActionButtonVisibility()
    }

    get canAnswerMessages() {
        return this._canAnswerMessages
    }
    
    private _updateBannerTypes() {
        // State colour is role-independent: procurer broadcast is blue, unanswered is
        // red/pink, answered is green.
        if (this._isBroadcast) {
            this.questionBanner.alertType = RHAlertBannerView.type.ProcurerBroadcast
        }
        else if (this.isAnswered) {
            this.questionBanner.alertType = RHAlertBannerView.type.AnsweredQuestion
        }
        else {
            this.questionBanner.alertType = RHAlertBannerView.type.UnansweredQuestion
        }
        this.answerBanner.alertType = RHAlertBannerView.type.AnsweredQuestion

    }
    
    private _updateActionButtonVisibility() {
        // Procurer broadcasts are informational: no "Vasta". "Kopeeri" stays but only
        // for the procurer (owner) — never in the tenderer's view — matching the
        // answered-question copy button's gating.
        const showWrite = !this._isLocked && !this._isBroadcast && !this.isAnswered && this._canAnswerMessages
        const showCopy = !this._isLocked &&
            this._canAnswerMessages &&
            !this._isAutomaticMessage &&
            (this._isBroadcast || this.isAnswered)
        RHNotificationBadge.applyDot(
            this.writeAnswerButton.titleLabel,
            showWrite,
            "Vastamata küsimus ootab vastust."
        )
        
        if (showWrite) {
            this.writeAnswerButton.addedAsSubviewToView(this)
        }
        else {
            this.writeAnswerButton.removeFromSuperview()
        }
        
        if (showCopy) {
            this.copyAnswerButton.addedAsSubviewToView(this)
        }
        else {
            this.copyAnswerButton.removeFromSuperview()
        }
        
        this.setNeedsLayout()
    }
    
    
    private _timestampWidth(label: UITextView, rowHeight: number, safetyBuffer: number) {
        return Math.min(
            this.bounds.width,
            label.intrinsicContentWidth(rowHeight) + safetyBuffer
        )
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const bounds = this.contentBoundsWithInset(0)
        const metadataRowFrame = bounds
            .rectangleByAddingY(RHSpacing.tiny)
            .rectangleWithHeight(RHQuestionAnswerPairViewTokens.metadataRowHeight)
            .rectangleWithInsets(
                RHCompanyQuestionsCardViewTokens.headerHorizontalPadding,
                RHCompanyQuestionsCardViewTokens.headerHorizontalPadding,
                0,
                0
            )
        // const questionDateWidth = this._timestampWidth(
        //     this.questionDateLabel,
        //     RHQuestionAnswerPairViewTokens.metadataRowHeight,
        //     RHQuestionAnswerPairViewTokens.metadataDateWidthSafetyBuffer
        // )
        const questionBubbleTrailingInset = IF(this.isAnswered)(() =>
            RHQuestionAnswerPairViewTokens.questionBubbleTrailingInset
        ).ELSE(() =>
            RHQuestionAnswerPairViewTokens.unansweredQuestionBubbleTrailingInset
        )
        const questionMetadataContentFrame = metadataRowFrame.rectangleWithInsets(
            0,
            questionBubbleTrailingInset,
            0,
            0
        )
        // const questionMetadataLabelWidth = Math.max(
        //     0,
        //     questionMetadataContentFrame.width - questionDateWidth - RHQuestionAnswerPairViewTokens.metadataGap
        // )

        // Metadata row — single combined title label (date merged into the title)
        // questionMetadataContentFrame.distributeViewsAlongWidth(
        //     [
        //         this.questionMetadataLabel,
        //         this.questionDateLabel
        //     ],
        //     undefined,
        //     RHQuestionAnswerPairViewTokens.metadataGap,
        //     [questionMetadataLabelWidth, questionDateWidth]
        // )
        questionMetadataContentFrame.assignedAsFrameOfView(this.questionMetadataLabel)

        metadataRowFrame
            // Question banner
            .rectangleWithInsets(0, questionBubbleTrailingInset, 0, 0)
            .rectangleForNextRow(RHSpacing.small, this.questionBanner)
            .assignedAsFrameOfView(this.questionBanner)
        
        // Question file list — positioned below the question bubble when present,
        // matching the same side-inset as the bubble itself.
        if (this.questionFilesListView.superview === this) {
            this.questionBanner.frame
                .rectangleWithInsets(0, questionBubbleTrailingInset, 0, 0)
                .rectangleForNextRow(RHSpacing.small, this.questionFilesListView)
                .assignedAsFrameOfView(this.questionFilesListView)
        }
        
        // Anchor for everything below the question bubble section.
        // If a question file list is present it sits below the banner, so the
        // answer section starts from that; otherwise it starts from the banner.
        const questionBubbleSectionBottomFrame = IF(this.questionFilesListView.superview === this)(() =>
            this.questionFilesListView.frame
        ).ELSE(() =>
            this.questionBanner.frame
        )
        
        // Answer section or answer button
        if (this.isAnswered && !this.answerSectionLabel.hidden) {
            const answerMetadataRowFrame = questionBubbleSectionBottomFrame
                .rectangleForNextRow(
                    RHSpacing.medium,
                    RHQuestionAnswerPairViewTokens.metadataRowHeight
                )
                .rectangleWithInsets(RHQuestionAnswerPairViewTokens.answerSectionLeadingInset, 0, 0, 0)
            const answerDateWidth = this._timestampWidth(
                this.answerDateLabel,
                RHQuestionAnswerPairViewTokens.metadataRowHeight,
                RHSpacing.small
            )
            const answerMetadataContentFrame = answerMetadataRowFrame.rectangleWithInsets(
                0,
                RHQuestionAnswerPairViewTokens.answerBubbleTrailingInset,
                0,
                0
            )
            const answerLabelWidth = Math.max(
                0,
                answerMetadataContentFrame.width - answerDateWidth - RHSpacing.small
            )
            
            answerMetadataContentFrame
                .distributeViewsAlongWidth(
                    [
                        this.answerSectionLabel,
                        this.answerDateLabel
                    ],
                    undefined,
                    RHSpacing.small,
                    [answerLabelWidth, answerDateWidth]
                )
            
            answerMetadataRowFrame
                .rectangleWithInsets(0, RHQuestionAnswerPairViewTokens.answerBubbleTrailingInset, 0, 0)
                .rectangleForNextRow(RHSpacing.small, this.answerBanner)
                .assignedAsFrameOfView(this.answerBanner)
            
            // Answer file list — positioned below the answer bubble when present.
            if (this.answerFilesListView.superview === this) {
                this.answerBanner.frame
                    .rectangleWithInsets(0, RHQuestionAnswerPairViewTokens.answerBubbleTrailingInset, 0, 0)
                    .rectangleForNextRow(RHSpacing.small, this.answerFilesListView)
                    .assignedAsFrameOfView(this.answerFilesListView)
            }
            
            // Copy button anchors below whichever is the last element in the answer section.
            const answerSectionBottomFrame = IF(this.answerFilesListView.superview === this)(() =>
                this.answerFilesListView.frame
            ).ELSE(() =>
                this.answerBanner.frame
            )
            
            if (this.copyAnswerButton.superview === this) {
                answerSectionBottomFrame
                    .rectangleForNextRow(
                        RHSpacing.small,
                        RHButtons.compactHeight
                    )
                    .rectangleWithWidth(this._actionButtonWidth(this.copyAnswerButton), 0)
                    .assignedAsFrameOfView(this.copyAnswerButton)
            }
            
        }
        else {
            // For a procurer broadcast the action slot holds "Kopeeri", not "Vasta".
            let actionButton = this.writeAnswerButton
            if (this._isBroadcast) {
                actionButton = this.copyAnswerButton
            }
            if (actionButton.superview === this) {
                questionBubbleSectionBottomFrame
                    .rectangleForNextRow(
                        RHSpacing.small,
                        RHButtons.compactHeight
                    )
                    .rectangleWithWidth(this._actionButtonWidth(actionButton), 0)
                    .assignedAsFrameOfView(actionButton)
            }

        }
        
        // Separator line at bottom
        const lastElement: UIView = IF(this._isBroadcast && this.copyAnswerButton.superview === this)(() =>
            this.copyAnswerButton as UIView
        ).ELSE_IF(this.isAnswered && this.copyAnswerButton.superview === this)(() =>
            this.copyAnswerButton as UIView
        ).ELSE_IF(this.isAnswered && this.answerFilesListView.superview === this)(() =>
            this.answerFilesListView as UIView
        ).ELSE_IF(this.isAnswered)(() =>
            this.answerBanner as UIView
        ).ELSE_IF(this.writeAnswerButton.superview === this)(() =>
            this.writeAnswerButton as UIView
        ).ELSE_IF(this.questionFilesListView.superview === this)(() =>
            this.questionFilesListView as UIView
        ).ELSE(() =>
            this.questionBanner as UIView
        )
        
        
        if (this.hasNextQuestion) {
            
            this.separatorLine.addedAsSubviewToView(this).frame = lastElement.frame
                .rectangleForNextRow(RHSpacing.medium, 1)
                .rectangleWithInsets(RHQuestionAnswerPairViewTokens.answerSectionLeadingInset * -1, 0, 0, 0)
                .rectangleByAddingX(-1)
                .rectangleWithWidth(bounds.width - 2, 0.5)
            
        }
        else {
            
            this.separatorLine.removeFromSuperview()
            
        }
        
    }
    
    _actionButtonWidth(button: RHButton): number {
        return Math.max(
            90,
            button.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHButtons.compactContentPadding * 2 +
            RHSpacing.small
        )
    }
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        
        const width = constrainingWidth || this.bounds.width
        const effectiveWidth = width > 0 ? width : 300
        const questionBubbleTrailingInset = IF(this.isAnswered)(() =>
            RHQuestionAnswerPairViewTokens.questionBubbleTrailingInset
        ).ELSE(() =>
            RHQuestionAnswerPairViewTokens.unansweredQuestionBubbleTrailingInset
        )
        const questionBannerWidth = Math.max(
            0,
            effectiveWidth
            - RHCompanyQuestionsCardViewTokens.headerHorizontalPadding * 2
            - questionBubbleTrailingInset
        )
        const answerBannerWidth = Math.max(
            0,
            effectiveWidth
            - RHCompanyQuestionsCardViewTokens.headerHorizontalPadding * 2
            - RHQuestionAnswerPairViewTokens.answerBubbleTrailingInset
        )
        
        let totalHeight = RHSpacing.tiny
            + RHQuestionAnswerPairViewTokens.metadataRowHeight
            + RHSpacing.small
            + this.questionBanner.intrinsicContentHeight(questionBannerWidth)
        
        if (this.questionFilesListView.superview === this) {
            totalHeight += RHSpacing.small
                + this.questionFilesListView.intrinsicContentHeight(questionBannerWidth)
        }
        
        if (this.isAnswered && !this.answerSectionLabel.hidden) {
            totalHeight += RHSpacing.medium
                + RHQuestionAnswerPairViewTokens.metadataRowHeight
                + RHSpacing.small
                + this.answerBanner.intrinsicContentHeight(answerBannerWidth)
            if (this.answerFilesListView.superview === this) {
                totalHeight += RHSpacing.small
                    + this.answerFilesListView.intrinsicContentHeight(answerBannerWidth)
            }
            if (this.copyAnswerButton.superview === this) {
                totalHeight += RHSpacing.small
                    + RHButtons.compactHeight
            }
        }
        else if (this.writeAnswerButton.superview === this || this.copyAnswerButton.superview === this) {
            // Procurer-only action slot: "Vasta" on an unanswered question, "Kopeeri"
            // on a broadcast.
            totalHeight += RHSpacing.small
                + RHButtons.compactHeight
        }
        
        if (this.hasNextQuestion) {
            totalHeight += RHSpacing.medium + 1
        }
        else {
            totalHeight += RHSpacing.medium
        }
        
        return totalHeight
        
    }
    
    // override intrinsicContentHeight(constrainingWidth?: number): number {
    //     const padding = this.core.paddingLength
    //     const width = constrainingWidth || this.bounds.width
    //     const effectiveWidth = width > 0 ? width : 300
    //
    //     let totalHeight = 0
    //
    //     // Metadata row
    //     totalHeight += padding + padding
    //     totalHeight += padding * 0.5
    //
    //     // Question banner
    //     const bannerWidth = effectiveWidth - padding * 2
    //     const bannerHeight = this.questionBanner.intrinsicContentHeight(bannerWidth)
    //     totalHeight += bannerHeight
    //
    //     if (this.isAnswered && !this.answerSectionLabel.hidden) {
    //         totalHeight += padding * 1.5
    //         totalHeight += this.questionMetadataLabel.frame.height
    //         totalHeight += padding * 0.5
    //
    //         const answerBannerHeight = this.answerBanner.intrinsicContentHeight(bannerWidth)
    //         totalHeight += answerBannerHeight
    //         totalHeight += padding * 0.25
    //         totalHeight += padding * 2
    //     }
    //     else {
    //         totalHeight += padding * 0.25
    //         totalHeight += padding * 2
    //     }
    //
    //     // Separator
    //     totalHeight += padding
    //     totalHeight += 1
    //
    //     return totalHeight
    // }


    static _bannerHTMLForAmendmentMessage(
        amendmentMessage: NonNullable<ProcurementQuestionAndAnswerThread["automaticMessage"]>,
        authorName?: string
    ): string {
        const changeExplanation = amendmentMessage.changeExplanation
        let message = "<div><strong>Automaatteavitus:</strong> Hanke andmeid muudeti.</div><br>"
        message += QuestionAnswerPairView._bannerHTMLLinesForChanges(amendmentMessage.changes).join("")
        if (authorName) {
            message += "<div><strong>Muutja:</strong> " + RHTextSanitizer.escapedHTML(authorName) + "</div>"
        }

        if (typeof changeExplanation === "string" && changeExplanation.trim()) {
            message += "<div><strong>Selgitus:</strong> " +
                RHTextSanitizer.escapedHTML(changeExplanation.trim()) +
                "</div>"
        }
        return message
    }


    static _bannerHTMLLinesForChanges(
        changes: RHProcurementAmendmentNotificationChange[]
    ): string[] {
        return (changes ?? []).flatMap(change => {
            if (
                change.kind === "value" &&
                typeof change.label === "string" &&
                typeof change.displayBefore === "string" &&
                typeof change.displayAfter === "string"
            ) {
                return [
                    "<div><strong>" +
                    RHTextSanitizer.escapedHTML(change.label) +
                    ":</strong> " +
                    RHTextSanitizer.escapedHTML(change.displayBefore) +
                    " <span aria-label=\"muutus väärtuseks\">→</span> " +
                    RHTextSanitizer.escapedHTML(change.displayAfter) +
                    "</div>"
                ]
            }
            if (change.kind === "summary" && typeof change.text === "string") {
                return [`<div>${RHTextSanitizer.escapedHTML(change.text)}</div>`]
            }
            return []
        })
    }

}
